import { useEffect, useRef, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import PdfViewerContainer from "../../../components/PdfViewerContainer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";

const FORM_NAME = "SBI_UPI_UNBLOCK";
const PDF_ENDPOINT = "/FillMyForm/SBI_UPI_UNBLOCK/Print.php";

const fields = [
  { name: "branch", label: "Branch", maxLength: 15, required: true },
  { name: "bcode", label: "Branch Code", maxLength: 15, required: true },
  { name: "cifno", label: "CIF Number", maxLength: 11, numeric: true },
  { name: "acno", label: "Account Number", maxLength: 11, numeric: true, required: true },
  { name: "name", label: "Applicant Name", maxLength: 40, alphaOnly: true, required: true },
  { name: "mobile", label: "Mobile Number", maxLength: 10, numeric: true, required: true },
  { name: "upi_id", label: "UPI ID", maxLength: 20, required: false },
  { name: "place", label: "Place", maxLength: 20, alphaOnly: true, required: true },

];

const initialFormData = {
  branch: "",
  bcode: "",
  cifno: "",
  acno: "",
  name: "",
  mobile: "",
  upi_id: "",
  place: "",

};

const tabs = ["Applicant Details", "Preview"];

function validateField(field, value) {
  const text = String(value ?? "").trim();

  if (field.required && !text) return `${field.label} is required.`;

  if (field.alphaOnly && text && !/^[A-Za-z ]+$/.test(text)) {
    return `${field.label} must contain alphabet letters only.`;
  }

  if (field.name === "cifno" && text && !/^\d{11}$/.test(text)) {
    return "CIF number must contain 11 digits.";
  }
  if (field.name === "acno" && text && !/^\d{11}$/.test(text)) {
    return "Account number must contain 11 digits.";
  }
  if (field.name === "mobile" && text && !/^\d{10}$/.test(text)) {
    return "Account number must contain 10 digits.";
  }


  return "";
}

export default function AEPS() {
  const [currentTab, setCurrentTab] = useState(0);
  const [maxAllowedTab, setMaxAllowedTab] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [pdfUrl, setPdfUrl] = useState("");
  const [showPdf, setShowPdf] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const nextButtonRef = useRef(null);

  useEffect(() => {
    if (currentTab !== 0) return undefined;

    const frame = window.requestAnimationFrame(() => {
      document.getElementById("branch")?.focus();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [currentTab]);

  useEffect(() => {
    const controller = new AbortController();

    async function loadAutofill() {
      try {
        const response = await fetch(
          `/backend/api/autofill.php?form=${encodeURIComponent(FORM_NAME)}`,
          {
            method: "GET",
            credentials: "include",
            cache: "no-store",
            headers: { Accept: "application/json" },
            signal: controller.signal,
          },
        );

        const result = await response.json();

        if (!response.ok || !result.success || !result.found || !result.data) {
          return;
        }

        setFormData((previous) => ({
          ...previous,
          branch: previous.branch || result.data.branch || "",
        }));
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("UPI-Unblock autofill error:", error);
        }
      }
    }

    loadAutofill();
    return () => controller.abort();
  }, []);

  useEffect(
    () => () => {
      if (pdfUrl?.startsWith("blob:")) URL.revokeObjectURL(pdfUrl);
    },
    [pdfUrl],
  );

  const updateField = (field, rawValue) => {
    let value = rawValue;

    if (field.numeric) {
      value = value.replace(/\D/g, "");
    }

    if (field.alphaOnly) {
      value = value.replace(/[^A-Za-z ]/g, "");
    }

    setFormData((previous) => ({
      ...previous,
      [field.name]: value,
    }));

    setErrors((previous) => ({
      ...previous,
      [field.name]: "",
    }));
  };

  const validateApplicantTab = () => {
    const nextErrors = {};

    fields.forEach((field) => {
      const message = validateField(field, formData[field.name]);
      if (message) nextErrors[field.name] = message;
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const goNext = (event) => {
    event?.preventDefault();
    event?.stopPropagation();

    if (!validateApplicantTab()) return;

    setCurrentTab(1);
    setMaxAllowedTab(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goPrevious = () => {
    setCurrentTab(0);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleLastFieldKeyDown = (event, fieldName) => {
    if (
      fieldName === "regtype" &&
      event.key === "Tab" &&
      !event.shiftKey
    ) {
      event.preventDefault();
      nextButtonRef.current?.focus();
    }
  };

    //Open in PDF Viewer
  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!validateApplicantTab()) {
      setCurrentTab(0);
      return;
    }

    setPdfLoading(true);
    setPdfError("");

    try {
      const body = new FormData();

      Object.entries(formData).forEach(([name, value]) => {
        body.append(name, String(value ?? ""));
      });

      const response = await fetch(PDF_ENDPOINT, {
        method: "POST",
        body,
        credentials: "include",
        headers: { Accept: "application/pdf" },
      });

      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || `PDF generation failed (${response.status}).`);
      }

      const contentType = response.headers.get("content-type") || "";

      if (!contentType.includes("application/pdf")) {
        const message = await response.text();
        throw new Error(message || "Print.php did not return a PDF file.");
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      setPdfUrl((previous) => {
        if (previous?.startsWith("blob:")) URL.revokeObjectURL(previous);
        return objectUrl;
      });

      setShowPdf(true);
    } catch (error) {
      console.error("SBI AEPS PDF error:", error);
      setPdfError(error.message || "Unable to generate PDF preview.");
    } finally {
      setPdfLoading(false);
    }
  };

//   //Open in New Tab

// const handleSubmit = (event) => {
//   event.preventDefault();
//   event.stopPropagation();

//   if (!validateApplicantTab()) {
//     setCurrentTab(0);
//     return;
//   }

//   // Create temporary HTML form
//   const form = document.createElement("form");

//   form.method = "POST";
//   form.action = PDF_ENDPOINT;
//   form.target = "_blank";
//   form.style.display = "none";

//   // Add all formData fields as POST variables
//   Object.entries(formData).forEach(([name, value]) => {
//     const input = document.createElement("input");

//     input.type = "hidden";
//     input.name = name;
//     input.value = String(value ?? "");

//     form.appendChild(input);
//   });

//   // Add form to page
//   document.body.appendChild(form);

//   // Submit to PHP and open response in new tab
//   form.submit();

//   // Remove temporary form
//   document.body.removeChild(form);
// };

  const renderField = (field) => {
    const error = errors[field.name];

    return (
      <div key={field.name} className="space-y-2">
        <Label htmlFor={field.name}>
          {field.label}
          {field.required && <span className="text-red-500"> *</span>}
        </Label>

        {field.type === "select" ? (
          <Select
            value={formData[field.name]}
            onValueChange={(value) => updateField(field, value)}
          >
            <SelectTrigger
              id={field.name}
              className={error ? "border-red-500" : ""}
              onKeyDown={(event) => handleLastFieldKeyDown(event, field.name)}
            >
              <SelectValue />
            </SelectTrigger>

            <SelectContent>
              {field.options.map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input
            id={field.name}
            name={field.name}
            type="text"
            value={formData[field.name]}
            maxLength={field.maxLength}
            placeholder={field.label}
            className={error ? "border-red-500" : ""}
            onChange={(event) => updateField(field, event.target.value)}
            onKeyDown={(event) => handleLastFieldKeyDown(event, field.name)}
          />
        )}

        {error && <p className="text-sm text-red-500">{error}</p>}
      </div>
    );
  };

  if (showPdf && pdfUrl) {
    return (
      <PdfViewerContainer
        pdfUrl={pdfUrl}
        title="SBI AEPS Registration Form"
        onBack={() => setShowPdf(false)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="mx-auto max-w-7xl">
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-3xl">
              🏦 SBI UPI Unblock Application Form
            </CardTitle>
            <p className="text-muted-foreground">
              Fill customer Unblock Application details carefully.
            </p>
          </CardHeader>
        </Card>

        <div className="mb-8 grid grid-cols-2 gap-3">
          {tabs.map((tab, index) => (
            <Button
              key={tab}
              type="button"
              variant={currentTab === index ? "default" : "outline"}
              disabled={index > maxAllowedTab}
              onClick={() => setCurrentTab(index)}
            >
              {index + 1}. {tab}
            </Button>
          ))}
        </div>

        {currentTab === 0 ? (
          <Card>
            <CardHeader>
              <CardTitle>👤 Account Holder Details</CardTitle>
            </CardHeader>

            <CardContent>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {fields.map(renderField)}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>📋 Preview</CardTitle>
            </CardHeader>

            <CardContent>
              <div className="overflow-hidden rounded-lg border">
                {fields.map((field) => (
                  <div
                    key={field.name}
                    className="grid grid-cols-2 border-b p-3 last:border-b-0"
                  >
                    <span className="font-medium">{field.label}</span>
                    <span className="break-words">
                      {field.name === "regtype"
                        ? "New Registration"
                        : formData[field.name] || "-"}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {pdfError && (
          <p className="mt-4 rounded-lg border border-red-300 bg-red-50 p-3 text-red-700">
            {pdfError}
          </p>
        )}

        <div className="mt-6 flex items-center justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={goPrevious}
            disabled={currentTab === 0}
          >
            ← Previous
          </Button>

          {currentTab === 1 ? (
            <Button
              type="button"
              ref={nextButtonRef}
              onClick={handleSubmit}
              disabled={pdfLoading}
            >
              {pdfLoading ? "Generating PDF..." : "Submit & Preview PDF"}
            </Button>
          ) : (
            <Button
              type="button"
              ref={nextButtonRef}
              onClick={goNext}
            >
              Preview
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
